/**
 * First-touch attribution for app signups: how did this visitor find us?
 *
 * On the first page load of a browser session, record the UTM params, the
 * external referrer (none for an internal navigation) and the landing path in
 * sessionStorage. Every fuguelab.com → app signup link (appSignupUrl) carries
 * them to fuguelab.app/auth as query params; the app keeps them through the
 * sign-in redirect and they land on the signup's CRM lead as `acquisition`.
 *
 * sessionStorage only: no cookie, no cross-site id, gone when the tab closes.
 * Storage that throws (private mode, blocked site data) means no attribution,
 * never a broken link.
 */

import { APP_URL } from '@/lib/public-trpc'

const STORAGE_KEY = 'fl_first_touch'
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const
const MAX_LEN = 300

export interface FirstTouch {
  utm: Partial<Record<(typeof UTM_KEYS)[number], string>>
  /** Origin + path of an external referrer; query strings are dropped. */
  referrer?: string
  landing: string
  firstSeenAt: string
}

/** An external referrer as origin + path, or undefined for none / our own site. */
export function externalReferrer(referrer: string, ownHost: string): string | undefined {
  if (!referrer) return undefined
  try {
    const url = new URL(referrer)
    if (url.host === ownHost) return undefined
    return `${url.origin}${url.pathname}`.slice(0, MAX_LEN)
  } catch {
    return undefined
  }
}

export function buildFirstTouch(
  search: string,
  referrer: string,
  ownHost: string,
  landing: string,
  now: Date
): FirstTouch {
  const params = new URLSearchParams(search)
  const utm: FirstTouch['utm'] = {}
  for (const key of UTM_KEYS) {
    const value = params.get(key)
    if (value) utm[key] = value.slice(0, MAX_LEN)
  }
  return {
    utm,
    referrer: externalReferrer(referrer, ownHost),
    landing: landing.slice(0, MAX_LEN),
    firstSeenAt: now.toISOString(),
  }
}

/** Record the session's first touch. Call once at boot; later pages keep the first. */
export function captureFirstTouch(): void {
  if (typeof window === 'undefined') return
  try {
    if (window.sessionStorage.getItem(STORAGE_KEY)) return
    const touch = buildFirstTouch(
      window.location.search,
      document.referrer,
      window.location.host,
      window.location.pathname,
      new Date()
    )
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(touch))
  } catch {
    // No storage: the signup simply carries no attribution.
  }
}

function readFirstTouch(): FirstTouch | undefined {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as FirstTouch) : undefined
  } catch {
    return undefined
  }
}

/** The query params an app signup link carries. */
export function attributionParams(touch: FirstTouch | undefined): URLSearchParams {
  const params = new URLSearchParams({ mode: 'signup' })
  if (!touch) return params
  for (const [key, value] of Object.entries(touch.utm)) if (value) params.set(key, value)
  if (touch.referrer) params.set('ref', touch.referrer)
  params.set('landing', touch.landing)
  params.set('first_seen', touch.firstSeenAt)
  return params
}

/** fuguelab.app sign-up URL carrying this session's first touch. Use for every app signup link. */
export function appSignupUrl(): string {
  const touch = typeof window === 'undefined' ? undefined : readFirstTouch()
  return `${APP_URL}/auth?${attributionParams(touch).toString()}`
}
